import React from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { AlertTriangle, ArrowRight, Bell, TrendingDown, Clock, FileWarning, RefreshCw } from 'lucide-react';
import api from '../lib/api';

const catIcon = (cat) => {
  if (cat === 'score_dip') return TrendingDown;
  if (cat === 'overdue_followup') return AlertTriangle;
  if (cat === 'followup_due') return Clock;
  if (cat === 'missing_signed_report') return FileWarning;
  return Bell;
};

const sevStyle = (s) => {
  if (s === 'critical') return { chip: 'bg-red-50 border-red-200 text-red-800', dot: 'bg-red-500' };
  if (s === 'warning') return { chip: 'bg-amber-50 border-amber-200 text-amber-800', dot: 'bg-amber-500' };
  return { chip: 'bg-brand-bg border-brand-border text-brand-indigo', dot: 'bg-brand-cyan' };
};

export const AlertsFeed = ({ compact = false }) => {
  const { user } = useApp();
  const [alerts, setAlerts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const list = await api.listAlerts();
      setAlerts(list);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, [user]);

  React.useEffect(() => { load(); }, [load]);

  const bySeverity = React.useMemo(() => {
    const c = { critical: 0, warning: 0, info: 0 };
    alerts.forEach((a) => { c[a.severity] = (c[a.severity] || 0) + 1; });
    return c;
  }, [alerts]);

  return (
    <Card className="border-brand-border">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-brand-indigo" />
            <div>
              <div className="text-sm font-semibold text-brand-indigo">RM Coaching Feed</div>
              <div className="text-[11px] text-muted-foreground">Alerts across your mapped team</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-[11px]">
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-500" /> {bySeverity.critical || 0}</span>
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-500" /> {bySeverity.warning || 0}</span>
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-brand-cyan" /> {bySeverity.info || 0}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={load} disabled={loading} className="h-8 text-brand-indigo">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''} mr-1.5`} /> Refresh
            </Button>
          </div>
        </div>

        {alerts.length === 0 && !loading && (
          <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
            All clear · no coaching alerts for your team right now.
          </div>
        )}

        <div className={`space-y-2 ${compact ? 'max-h-[380px] overflow-y-auto pr-1' : ''}`}>
          {alerts.slice(0, compact ? 10 : 999).map((a) => {
            const Icon = catIcon(a.category);
            const st = sevStyle(a.severity);
            const linkTo = a.session_id ? `/clients/${a.client_id}/mri/${a.session_id}` : a.client_id ? `/clients/${a.client_id}` : null;
            return (
              <div key={a.id} className={`border rounded-md p-3 flex gap-3 ${st.chip}`}>
                <div className={`h-8 w-8 rounded-md flex items-center justify-center ${st.dot} text-white shrink-0`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-sm text-brand-indigo">{a.title}</div>
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-brand-border">{a.category.replace(/_/g, ' ')}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{a.detail}</div>
                  {a.rm_name && <div className="text-[11px] mt-1 text-brand-indigo/70">RM: <span className="font-semibold">{a.rm_name}</span></div>}
                </div>
                {linkTo && (
                  <Link to={linkTo} className="text-xs font-semibold text-brand-indigo hover:underline flex items-center gap-1 shrink-0 self-center">Open <ArrowRight className="h-3 w-3" /></Link>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default AlertsFeed;
